import cn from 'classnames'

import { ITableColumn } from 'partner360/components/financialTable'
import { QbrQuarters } from 'partner360/components/QBR/models/qbrQuarters'
import { ICompetitorsSobChangeLog } from 'partner360/models/competitorsSob/ICompetitorsSobChangeLog'
import { ISobCompetitorsGroup } from 'partner360/models/ISobCompetitorsGroup'

import styles from '../components/table/CompetitorTable.module.scss'
import { NameColumn } from '../components/table/nameColumn/NameColumn'
import { SobCompetitorCell } from '../components/table/sobCompetitorCell/SobCompetitorCell'
import { SobCompetitorItems } from '../components/table/sobCompetitorItems/SobCompetitorItems'
import { TRANSLATION_KEYS } from '../translations'

import { isEditableCondition } from './isEditableCondition'

interface IGetColumn {
  onChange?: (entryId: string, competitorId: string, field: string) => (value: number) => void
  expandedKeys: string[]
  otherKey: string
  isDisplaySellThru: boolean
  selectedPeriodFQuarters: number[]
  changes: ICompetitorsSobChangeLog[]
}
export const getColumns = ({
  onChange,
  expandedKeys,
  otherKey,
  isDisplaySellThru,
  selectedPeriodFQuarters,
  changes,
}: IGetColumn): ITableColumn<ISobCompetitorsGroup>[] => {
  const isEditable = isEditableCondition({ otherKey, expandedKeys })

  const prevYearColumns: ITableColumn<ISobCompetitorsGroup>[] = [
    {
      key: 'PrevYear',
      title: TRANSLATION_KEYS.table.columns.FY,
      isTranslatable: true,
      width: 200,
      renderFn: (item) => {
        return (
          <div className={cn({ [styles.total]: true })}>
            <SobCompetitorItems
              item={item}
              isDisplaySellThru={isDisplaySellThru}
              renderContent={(comp) => (
                <SobCompetitorCell
                  showSellThru={isDisplaySellThru}
                  isEditable={false}
                  isTotal={true}
                  sob={comp.PrevYearFYActualsSob}
                  sellThru={comp.PrevYearFYActuals}
                  yoySob={comp.PrevYearFYYoYActualsSob}
                  onChangeSob={onChange?.(item.EntryId, comp.CompetitorID, 'PrevYearFYActualsSob')}
                />
              )}
            />
          </div>
        )
      },
    },
    {
      key: 'PrevYearQ1',
      title: TRANSLATION_KEYS.table.columns.Q1,
      isTranslatable: true,
      width: 200,
      renderFn: (item) => {
        return (
          <SobCompetitorItems
            item={item}
            isDisplaySellThru={isDisplaySellThru}
            renderContent={(comp) => (
              <SobCompetitorCell
                showSellThru={isDisplaySellThru}
                isEditable={isEditable(comp, item)}
                isOtherCompetitor={comp.CompetitorID === otherKey}
                sob={comp.PrevYearQ1ActualsSob}
                sellThru={comp.PrevYearQ1Actuals}
                yoySob={comp.PrevYearQ1YoYActualsSob}
                qoqSob={comp.PrevYearQ1QoQActualsSob}
                onChangeSob={onChange?.(item.EntryId, comp.CompetitorID, 'PrevYearQ1ActualsSob')}
              />
            )}
          />
        )
      },
    },
    {
      key: 'PrevYearQ2',
      title: TRANSLATION_KEYS.table.columns.Q2,
      isTranslatable: true,
      width: 200,
      renderFn: (item) => {
        return (
          <SobCompetitorItems
            item={item}
            isDisplaySellThru={isDisplaySellThru}
            renderContent={(comp) => (
              <SobCompetitorCell
                showSellThru={isDisplaySellThru}
                isEditable={isEditable(comp, item)}
                isOtherCompetitor={comp.CompetitorID === otherKey}
                sob={comp.PrevYearQ2ActualsSob}
                sellThru={comp.PrevYearQ2Actuals}
                yoySob={comp.PrevYearQ2YoYActualsSob}
                qoqSob={comp.PrevYearQ2QoQActualsSob}
                onChangeSob={onChange?.(item.EntryId, comp.CompetitorID, 'PrevYearQ2ActualsSob')}
              />
            )}
          />
        )
      },
    },
    {
      key: 'PrevYearQ3',
      title: TRANSLATION_KEYS.table.columns.Q3,
      isTranslatable: true,
      width: 200,
      renderFn: (item) => {
        return (
          <SobCompetitorItems
            item={item}
            isDisplaySellThru={isDisplaySellThru}
            renderContent={(comp) => (
              <SobCompetitorCell
                showSellThru={isDisplaySellThru}
                isEditable={isEditable(comp, item)}
                isOtherCompetitor={comp.CompetitorID === otherKey}
                sob={comp.PrevYearQ3ActualsSob}
                sellThru={comp.PrevYearQ3Actuals}
                yoySob={comp.PrevYearQ3YoYActualsSob}
                qoqSob={comp.PrevYearQ3QoQActualsSob}
                onChangeSob={onChange?.(item.EntryId, comp.CompetitorID, 'PrevYearQ3ActualsSob')}
              />
            )}
          />
        )
      },
    },
    {
      key: 'PrevYearQ4',
      title: TRANSLATION_KEYS.table.columns.Q4,
      isTranslatable: true,
      width: 200,
      renderFn: (item) => {
        return (
          <SobCompetitorItems
            item={item}
            isDisplaySellThru={isDisplaySellThru}
            renderContent={(comp) => (
              <SobCompetitorCell
                showSellThru={isDisplaySellThru}
                isEditable={isEditable(comp, item)}
                isOtherCompetitor={comp.CompetitorID === otherKey}
                sob={comp.PrevYearQ4ActualsSob}
                sellThru={comp.PrevYearQ4Actuals}
                yoySob={comp.PrevYearQ4YoYActualsSob}
                qoqSob={comp.PrevYearQ4QoQActualsSob}
                onChangeSob={onChange?.(item.EntryId, comp.CompetitorID, 'PrevYearQ4ActualsSob')}
              />
            )}
          />
        )
      },
    },
  ]

  const currentYearColumns: ITableColumn<ISobCompetitorsGroup>[] = [
    {
      key: 'CurrentYear',
      title: TRANSLATION_KEYS.table.columns.FY,
      isTranslatable: true,
      width: 200,
      renderFn: (item) => {
        return (
          <div className={cn({ [styles.total]: true })}>
            <SobCompetitorItems
              item={item}
              isDisplaySellThru={isDisplaySellThru}
              renderContent={(comp) => (
                <SobCompetitorCell
                  showSellThru={isDisplaySellThru}
                  isEditable={false}
                  isTotal={true}
                  sob={comp.CurrentYearFYActualsSob}
                  sellThru={comp.CurrentYearFYActuals}
                  yoySob={comp.CurrentYearFYYoYActualsSob}
                  onChangeSob={onChange?.(
                    item.EntryId,
                    comp.CompetitorID,
                    'CurrentYearFYActualsSob'
                  )}
                />
              )}
            />
          </div>
        )
      },
    },
  ]

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q1)) {
    currentYearColumns.push({
      key: 'CurrentYearQ1',
      title: TRANSLATION_KEYS.table.columns.Q1,
      isTranslatable: true,
      width: 200,
      renderFn: (item) => {
        return (
          <SobCompetitorItems
            item={item}
            isDisplaySellThru={isDisplaySellThru}
            renderContent={(comp) => (
              <SobCompetitorCell
                showSellThru={isDisplaySellThru}
                isEditable={isEditable(comp, item)}
                isOtherCompetitor={comp.CompetitorID === otherKey}
                sob={comp.CurrentYearQ1ActualsSob}
                sellThru={comp.CurrentYearQ1Actuals}
                yoySob={comp.CurrentYearQ1YoYActualsSob}
                qoqSob={comp.CurrentYearQ1QoQActualsSob}
                onChangeSob={onChange?.(item.EntryId, comp.CompetitorID, 'CurrentYearQ1ActualsSob')}
              />
            )}
          />
        )
      },
    })
  }

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q2)) {
    currentYearColumns.push({
      key: 'CurrentYearQ2',
      title: TRANSLATION_KEYS.table.columns.Q2,
      isTranslatable: true,
      width: 200,
      renderFn: (item) => {
        return (
          <SobCompetitorItems
            item={item}
            isDisplaySellThru={isDisplaySellThru}
            renderContent={(comp) => (
              <SobCompetitorCell
                showSellThru={isDisplaySellThru}
                isEditable={isEditable(comp, item)}
                isOtherCompetitor={comp.CompetitorID === otherKey}
                sob={comp.CurrentYearQ2ActualsSob}
                sellThru={comp.CurrentYearQ2Actuals}
                yoySob={comp.CurrentYearQ2YoYActualsSob}
                qoqSob={comp.CurrentYearQ2QoQActualsSob}
                onChangeSob={onChange?.(item.EntryId, comp.CompetitorID, 'CurrentYearQ2ActualsSob')}
              />
            )}
          />
        )
      },
    })
  }

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q3)) {
    currentYearColumns.push({
      key: 'CurrentYearQ3',
      title: TRANSLATION_KEYS.table.columns.Q3,
      isTranslatable: true,
      width: 200,
      renderFn: (item) => {
        return (
          <SobCompetitorItems
            item={item}
            isDisplaySellThru={isDisplaySellThru}
            renderContent={(comp) => (
              <SobCompetitorCell
                showSellThru={isDisplaySellThru}
                isEditable={isEditable(comp, item)}
                isOtherCompetitor={comp.CompetitorID === otherKey}
                sob={comp.CurrentYearQ3ActualsSob}
                sellThru={comp.CurrentYearQ3Actuals}
                yoySob={comp.CurrentYearQ3YoYActualsSob}
                qoqSob={comp.CurrentYearQ3QoQActualsSob}
                onChangeSob={onChange?.(item.EntryId, comp.CompetitorID, 'CurrentYearQ3ActualsSob')}
              />
            )}
          />
        )
      },
    })
  }

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q4)) {
    currentYearColumns.push({
      key: 'CurrentYearQ4',
      title: TRANSLATION_KEYS.table.columns.Q4,
      isTranslatable: true,
      width: 200,
      renderFn: (item) => {
        return (
          <SobCompetitorItems
            item={item}
            isDisplaySellThru={isDisplaySellThru}
            renderContent={(comp) => (
              <SobCompetitorCell
                showSellThru={isDisplaySellThru}
                isEditable={isEditable(comp, item)}
                isOtherCompetitor={comp.CompetitorID === otherKey}
                sob={comp.CurrentYearQ4ActualsSob}
                sellThru={comp.CurrentYearQ4Actuals}
                yoySob={comp.CurrentYearQ4YoYActualsSob}
                qoqSob={comp.CurrentYearQ4QoQActualsSob}
                onChangeSob={onChange?.(item.EntryId, comp.CompetitorID, 'CurrentYearQ4ActualsSob')}
              />
            )}
          />
        )
      },
    })
  }
  return [
    {
      key: 'name',
      title: '',
      width: 250,
      renderFn: (item) => {
        return <NameColumn item={item} isDisplaySellThru={isDisplaySellThru} changes={changes} />
      },
    },
    ...prevYearColumns,

    ...currentYearColumns,
  ]
}
